import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import utils from "../../universal/utils";
import adminFetch from "../../universal/adminFetch";

const basePath = utils.getEdgeBasePath();

export default function SetPlayerFounderBadgeDialog({
  openState,
  userContext,
  adminUserId,
  onClose,
  onComplete,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [founderStatus, setFounderStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  useEffect(() => {
    if (!openState || !userContext?.row?.userId || !adminUserId) return;
    setSubmitting(false);
    setError("");
    setFounderStatus(null);
    setLoadingStatus(true);

    adminFetch(
      `${basePath}/api/v7/admin/membership/founder-badge/status/${userContext.row.userId}/adpass/${adminUserId}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) {
          setFounderStatus(data);
        } else {
          setError(data.error || data.err || "Failed to load founder status");
        }
      })
      .catch((err) => {
        setError(err.message || "Failed to load founder status");
      })
      .finally(() => setLoadingStatus(false));
  }, [openState, userContext?.row?.userId, adminUserId]);

  if (!userContext?.row) return null;

  const isFounder = founderStatus?.isFounder === true;

  const handleGrant = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/founder-badge/grant/adpass/${adminUserId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ targetUserId: userContext.row.userId }),
        }
      );
      const data = await res.json();
      if (data.ok && data.granted) {
        onComplete?.();
        onClose();
      } else if (data.reason === "already_founder") {
        setError(`Already a Founder (#${data.number})`);
      } else {
        setError(data.error || data.err || data.reason || "Failed to grant");
      }
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/founder-badge/revoke/adpass/${adminUserId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ targetUserId: userContext.row.userId }),
        }
      );
      const data = await res.json();
      if (data.ok && data.revoked) {
        onComplete?.();
        onClose();
      } else {
        setError(data.error || data.err || data.reason || "Failed to revoke");
      }
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={openState}
      onClose={submitting ? undefined : onClose}
      aria-labelledby="founder-badge-dialog-title"
    >
      <DialogTitle id="founder-badge-dialog-title">
        Founder Badge — {userContext.row.nickname}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Grant or revoke the Founder badge for this user. Admin-granted badges
          bypass the public cap.
        </DialogContentText>

        <Typography variant="body2" style={{ marginBottom: 8 }}>
          <strong>User ID:</strong> {userContext.row.userId}
        </Typography>

        {loadingStatus && <CircularProgress size={20} />}

        {!loadingStatus && founderStatus && (
          <Typography
            variant="body2"
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              backgroundColor: isFounder
                ? "rgba(192, 160, 80, 0.1)"
                : "rgba(255,255,255,0.05)",
              marginBottom: 8,
            }}
          >
            {isFounder
              ? `Currently a Founder (#${founderStatus.number}, since ${new Date(founderStatus.grantedAt).toLocaleDateString()})`
              : "Not a Founder"}
          </Typography>
        )}

        {error && (
          <Typography variant="body2" style={{ marginTop: 8, color: "#d32f2f" }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={submitting}>
          Cancel
        </Button>
        {isFounder ? (
          <Button
            onClick={handleRevoke}
            style={{ color: "#d32f2f" }}
            disabled={submitting || loadingStatus || !founderStatus}
          >
            {submitting ? "Revoking..." : "Revoke Founder Badge"}
          </Button>
        ) : (
          <Button
            onClick={handleGrant}
            style={{ color: "#C0A050" }}
            disabled={submitting || loadingStatus || !founderStatus}
          >
            {submitting ? "Granting..." : "Grant Founder Badge"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
